import { useState } from "react";
import { Lock, Eye, EyeOff, Mail } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";

const PcCareLogin = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo =
    new URLSearchParams(location.search).get("redirect") || "/checkout";

  const getBaseUrl = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const backendRoot = process.env.REACT_APP_BACKEND;
    const apiBase = process.env.REACT_APP_API_BASE_URL;
    return (backendUrl || apiBase || backendRoot || "http://localhost:8000")
      .trim()
      .replace(/([^:]\/)\/+/g, "$1")
      .replace(/\/+$/, "");
  };

  const extractErrorMessage = (data, fallback) => {
    if (typeof data === "string" && data.trim()) return data;
    if (Array.isArray(data?.message) && data.message.length) {
      return data.message.join(", ");
    }
    if (typeof data?.message === "string" && data.message.trim()) {
      return data.message;
    }
    if (typeof data?.error === "string" && data.error.trim()) {
      return data.error;
    }
    return fallback;
  };

  const saveLoginSession = (data, normalizedEmail) => {
    if (data?.access_token) {
      localStorage.setItem("token", data.access_token);
    }

    const decodeTokenId = () => {
      try {
        const token = data?.access_token || localStorage.getItem("token");
        if (!token) return null;
        const payload = token.split(".")[1];
        const decoded = JSON.parse(atob(payload));
        return (
          decoded?.id ||
          decoded?.user_id ||
          decoded?.customer_id ||
          decoded?.customerId ||
          decoded?.userId ||
          null
        );
      } catch {
        return null;
      }
    };

    const resolvedId =
      data?.id ||
      data?.user?.id ||
      data?.data?.id ||
      data?.customer_id ||
      data?.customer?.id ||
      decodeTokenId();

    const userPayload = {
      id: resolvedId,
      name: data?.name || data?.user?.name || data?.data?.name,
      email: normalizedEmail,
    };
    localStorage.setItem("user", JSON.stringify(userPayload));
    window.dispatchEvent(new Event("userUpdated"));
  };

  const runLoginRequest = async (normalizedEmail, passwordValue) => {
    const baseUrl = getBaseUrl();
    const res = await fetch(`${baseUrl}/customers/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: normalizedEmail, password: passwordValue }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        ok: false,
        status: res.status,
        message: extractErrorMessage(data, "Login failed"),
      };
    }

    return { ok: true, data };
  };

  const handleLogin = async () => {
    setErrorMsg("");

    if (!email || !password) {
      setErrorMsg("Please enter required fields");
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
    if (!emailOk) {
      setErrorMsg("Please enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      const result = await runLoginRequest(normalizedEmail, password);

      if (!result.ok) {
        const msg = result.message || "Login failed";
        const lowerMsg = msg.toLowerCase();

        if (
          result.status === 403 ||
          lowerMsg.includes("not verified") ||
          lowerMsg.includes("verify otp")
        ) {
          navigate("/verify-otp", {
            state: {
              email: normalizedEmail,
              password,
              message: "Account not verified. Enter OTP to activate account.",
            },
          });
          return;
        }

        if (result.status === 401 || lowerMsg.includes("unauthorized")) {
          throw new Error("Account not found or password incorrect.");
        }

        throw new Error(msg);
      }

      saveLoginSession(result.data, normalizedEmail);
      setErrorMsg("Login successful");
      navigate(redirectTo);
    } catch (err) {
      setErrorMsg(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen  flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-6xl bg-white rounded-2xl shadow-2xl grid grid-cols-1 md:grid-cols-2 overflow-hidden">
        {/* ================= LEFT VIDEO SECTION ================= */}
        <div className="hidden md:block relative">
          <video
            src="/ani.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          />
        </div>

        {/* ================= RIGHT LOGIN SECTION ================= */}
        <div className="p-6 sm:p-12 flex flex-col justify-center bg-white">
          <h2 className="text-3xl font-bold text-center text-[#1F3C88] mb-4">
            Login
          </h2>

          {/* Email */}
          <div className="mb-4">
            <label className="text-sm text-gray-600">Email address</label>
            <div className="relative mt-1">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrorMsg("");
                }}
                placeholder="Enter email"
                className="w-full pl-10 pr-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#1F3C88] outline-none"
              />
            </div>
          </div>

          {/* Password */}
          <div className="mb-4">
            <label className="text-sm text-gray-600">Password</label>
            <div className="relative mt-1">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErrorMsg("");
                }}
                placeholder="********"
                className="w-full pl-10 pr-10 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#1F3C88] outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#1F3C88]"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Forgot */}
          <div className="text-right text-sm mb-4">
            <Link to="/password" className="text-[#E63946] hover:underline">
              Forgot Password / Verify
            </Link>
          </div>

          {errorMsg && (
            <div className={`text-center text-sm mb-2 ${errorMsg.toLowerCase().includes("successful") ? "text-green-600" : "text-red-500"}`}>
              {errorMsg}{" "}
              {errorMsg.toLowerCase().includes("create an account") && (
                <Link to="/registation" className="text-[#1F3C88] font-semibold hover:underline">
                  Create account
                </Link>
              )}
            </div>
          )}

          {/* Login Button */}
          <div className="flex justify-center">
            <button
              onClick={handleLogin}
              disabled={loading}
              className="px-8 py-2 bg-[#1F3C88] hover:bg-[#16306e] text-white text-sm font-medium rounded-full shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-60"
            >
              {loading ? "Logging in..." : "Log in"}
            </button>
          </div>

          {/* Footer */}
          <p className="text-center text-xl text-gray-500 mt-2">
            New to PC Care?{" "}
            <Link to="/registation" className="text-[#1F3C88] font-semibold hover:underline">
              Create an account
            </Link>
          </p>

          <p className="text-center text-xs text-gray-400 mt-2">
            <Link to="/contact" className="hover:underline">
              Need Help? Contect Now
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PcCareLogin;
